import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ActionItem {
  label: string;
  icon: string;
  color: string;
  danger?: boolean;
  onClick: () => void;
}

interface CustomerActionGridProps {
  onNewSale: () => void;
  onAddPayment: () => void;
  onAddNote: () => void;
  onEdit: () => void;
  onEnforce: () => void;
  onSubmitToEnforcement: () => void;
  canEnforce: boolean;
  activeEnforcement: any;
  showMoreSheet: boolean;
  onToggleMore: () => void;
  onCloseMore: () => void;
  phone: string;
  secondPhone?: string;
  onDeleteCustomer?: () => void;
  onDebtAdjustment?: () => void;
  onAddPaymentPromise?: () => void;
  onReport?: () => void;
}

export default function CustomerActionGrid({
  onNewSale,
  onAddPayment,
  onAddNote,
  onEdit,
  onEnforce,
  canEnforce,
  activeEnforcement,
  showMoreSheet,
  onToggleMore,
  onCloseMore,
  phone,
  secondPhone,
  onDeleteCustomer,
  onDebtAdjustment,
  onAddPaymentPromise,
  onReport,
}: CustomerActionGridProps) {
  const navigate = useNavigate();
  const cleanPhone = (p: string) => (p || '').replace(/\s/g, '');

  // Desktop dropdown state
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  // Mobile more actions
  const moreActions: ActionItem[] = [
    { label: 'Müşteriyi Düzenle', icon: 'ri-edit-line', color: 'text-secondary-700', onClick: onEdit },
    {
      label: 'İcraya Aktar',
      icon: 'ri-scales-3-line',
      color: 'text-red-600',
      danger: true,
      onClick: onEnforce,
    },
  ];

  if (onReport) {
    moreActions.push({
      label: 'Özet Raporu Görüntüle',
      icon: 'ri-file-chart-line',
      color: 'text-amber-600',
      onClick: onReport,
    });
  }

  moreActions.push(
    { label: 'Hesap Ekstresi Yazdır', icon: 'ri-printer-line', color: 'text-secondary-700', onClick: () => navigate('/reports') },
    { label: 'Telefonla Ara', icon: 'ri-phone-line', color: 'text-emerald-600', onClick: () => window.open(`tel:${cleanPhone(phone)}`, '_self') },
    { label: 'WhatsApp Mesajı Gönder', icon: 'ri-whatsapp-line', color: 'text-emerald-600', onClick: () => window.open(`https://wa.me/90${cleanPhone(phone).slice(1)}`, '_blank') },
  );

  if (onDeleteCustomer) {
    moreActions.push({
      label: 'Müşteriyi Sil',
      icon: 'ri-delete-bin-line',
      color: 'text-red-600',
      danger: true,
      onClick: onDeleteCustomer,
    });
  }

  if (onDebtAdjustment) {
    moreActions.push({
      label: 'Borç Düzeltme',
      icon: 'ri-scales-3-line',
      color: 'text-amber-700',
      onClick: onDebtAdjustment,
    });
  }

  // Desktop dropdown items
  const dropdownItems: ActionItem[] = [
    { label: 'Müşteriyi Düzenle', icon: 'ri-edit-line', color: 'text-secondary-700', onClick: onEdit },
    {
      label: 'İcraya Aktar',
      icon: 'ri-scales-3-line',
      color: 'text-red-600',
      danger: true,
      onClick: onEnforce,
    },
  ];

  if (onDebtAdjustment) {
    dropdownItems.push({
      label: 'Borç Düzeltme',
      icon: 'ri-scales-3-line',
      color: 'text-amber-700',
      onClick: onDebtAdjustment,
    });
  }

  if (onDeleteCustomer) {
    dropdownItems.push({
      label: 'Müşteriyi Sil',
      icon: 'ri-delete-bin-line',
      color: 'text-red-600',
      danger: true,
      onClick: onDeleteCustomer,
    });
  }

  return (
    <div className="w-full max-w-full">
      {/* Desktop: horizontal row of buttons + dropdown */}
      <div className="hidden lg:flex items-center gap-2 ml-auto flex-shrink-0">
        {onReport && (
          <button className="flex items-center gap-2 cursor-pointer whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 active:scale-[0.97]" onClick={onReport}>
            <i className="ri-file-chart-line"></i><span>Özet Rapor</span>
          </button>
        )}
        <button className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap" onClick={onNewSale}>
          <i className="ri-add-line"></i><span>Yeni Satış</span>
        </button>
        <button className="btn-accent flex items-center gap-2 cursor-pointer whitespace-nowrap" onClick={onAddPayment}>
          <i className="ri-bank-card-line"></i><span>Ödeme Ekle</span>
        </button>
        {onAddPaymentPromise && (
          <button className="flex items-center gap-2 cursor-pointer whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 active:scale-[0.97]" onClick={onAddPaymentPromise}>
            <i className="ri-hand-heart-line"></i><span>Ödeme Sözü Ekle</span>
          </button>
        )}
        <button className="btn-outline flex items-center gap-2 cursor-pointer whitespace-nowrap" onClick={onAddNote}>
          <i className="ri-file-list-3-line"></i><span>Senet Ekle</span>
        </button>

        {/* Desktop More dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            className="btn-outline flex items-center justify-center cursor-pointer whitespace-nowrap w-10 h-10 p-0 rounded-lg"
            onClick={() => setShowDropdown(!showDropdown)}
            title="Daha Fazla"
          >
            <i className="ri-more-2-line text-lg"></i>
          </button>
          {showDropdown && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-background-50 rounded-xl border border-background-200 shadow-lg z-[60] py-1 animate-dropdown-in overflow-hidden">
              {dropdownItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => { setShowDropdown(false); item.onClick(); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    item.danger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    item.danger ? 'bg-red-50' : 'bg-background-100'
                  }`}>
                    <i className={`${item.icon} text-sm ${item.color}`} />
                  </span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile: 2-col grid + More button */}
      <div className="lg:hidden space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onNewSale}
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium cursor-pointer active:scale-[0.97] transition-transform whitespace-nowrap"
            style={{ minHeight: 48 }}
          >
            <i className="ri-add-line text-lg"></i>
            <span>Yeni Satış</span>
          </button>
          <button
            onClick={onAddPayment}
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-accent-500 text-background-50 text-sm font-medium cursor-pointer active:scale-[0.97] transition-transform whitespace-nowrap"
            style={{ minHeight: 48 }}
          >
            <i className="ri-bank-card-line text-lg"></i>
            <span>Ödeme Ekle</span>
          </button>
        </div>

        {/* Second row: Payment Promise + Note */}
        <div className="grid grid-cols-2 gap-2">
          {onAddPaymentPromise ? (
            <button
              onClick={onAddPaymentPromise}
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-emerald-500 text-background-50 text-sm font-medium cursor-pointer active:scale-[0.97] transition-transform whitespace-nowrap"
              style={{ minHeight: 48 }}
            >
              <i className="ri-hand-heart-line text-lg"></i>
              <span>Ödeme Sözü Ekle</span>
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={onAddNote}
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-background-300 bg-background-50 text-foreground-700 text-sm font-medium cursor-pointer active:scale-[0.97] transition-transform whitespace-nowrap"
            style={{ minHeight: 48 }}
          >
            <i className="ri-file-list-3-line text-lg"></i>
            <span>Senet Ekle</span>
          </button>
        </div>

        <button
          onClick={onToggleMore}
          className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-background-300 bg-background-50 text-foreground-700 text-sm font-medium cursor-pointer active:scale-[0.97] transition-transform whitespace-nowrap"
          style={{ minHeight: 48 }}
        >
          <i className="ri-more-2-line text-lg"></i>
          <span>Daha Fazla İşlem</span>
        </button>
      </div>

      {/* Mobile More Actions Bottom Sheet */}
      {showMoreSheet && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[65] lg:hidden animate-fade-in"
            onClick={onCloseMore}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[66] lg:hidden bg-background-50 rounded-t-2xl animate-slide-up-sheet"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1.5 rounded-full bg-background-300" />
            </div>
            <div className="px-4 pb-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-1 mb-2">
                İşlemler
              </p>
            </div>
            <div className="px-3">
              {moreActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => { onCloseMore(); action.onClick(); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-1 text-sm font-medium cursor-pointer active:scale-[0.98] transition-transform ${
                    action.danger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                  style={{ minHeight: 48 }}
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    action.danger ? 'bg-red-50' : 'bg-background-100'
                  }`}>
                    <i className={`${action.icon} text-base ${action.color}`} />
                  </span>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}